import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { UrlSignature } from '../common/apiEndpoint';
import { SuperloginService } from '../superlogin/superlogin.service';

interface ApplicantRegistryDto {
  applicantId: number;
  firstName: string;
  lastName: string;
  middleName: string;
  email: string;
  phoneNumber: string;
  alternatePhone: string;
  gender: string;
  dateOfBirth: string;
  nationality: string;
  applicantType: string;
  programmeAppliedFor: string;
  programmeType: string;
  preferredStudyMode: string;
  academicYear: string;
  applicationStatus: string;
  applicationDate: string;
  reviewDate: string;
  decisionDate: string;
  reviewerComments: string;
  semester: string;
  photoPath: string;
  wassceIndexNumber: string;
  previousSchool: string;
  createdAt: string;
  updatedAt: string;
}

interface ApiResponse {
  success: boolean;
  count: number;
  data: ApplicantRegistryDto[];
}

interface StatusUpdatePayload {
  applicantId: number;
  applicationStatus: string;
  reviewerComments: string;
  updatedBy: string;
}

@Component({
  selector: 'app-applicationreview',
  templateUrl: './applicationreview.component.html',
  styleUrls: ['./applicationreview.component.css']
})
export class ApplicationreviewComponent implements OnInit {

  allApplicants: ApplicantRegistryDto[] = [];
  filteredApplicants: ApplicantRegistryDto[] = [];
  pagedApplicants: ApplicantRegistryDto[] = [];

  isLoading = false;
  errorMessage = '';
  searchQuery = '';
  activeFilter = 'ALL';

  currentPage = 1;
  pageSize = 10;
  totalPages = 1;
  pages: number[] = [];

  expandedRowId: number | null = null;


  imgErrors: Set<number> = new Set();
  lightboxUrl: string | null = null;
  lightboxName: string = '';


  // Modal state
  showModal = false;
  modalAction: 'Accepted' | 'Rejected' | 'Under Review' | '' = '';
  modalApplicant: ApplicantRegistryDto | null = null;
  reviewerComments = '';
  isSubmitting = false;
  submitSuccess = '';
  submitError = '';

  constructor(
    private http: HttpClient,
    private urls: UrlSignature,
    private authService: SuperloginService
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  getHeaders(): HttpHeaders {
    const token = this.authService.getToken() || '';
    return new HttpHeaders({
      'Authorization': 'Bearer ' + token,
      'Content-Type': 'application/json'
    });
  }


  onImgError(applicantId: number): void {
  this.imgErrors.add(applicantId);
}

hasPhoto(a: ApplicantRegistryDto): boolean {
  return !!a.photoPath && !this.imgErrors.has(a.applicantId);
}



openLightbox(url: string, name: string, event: Event): void {
  event.stopPropagation();
  this.lightboxUrl = url;
  this.lightboxName = name;
}

closeLightbox(): void {
  this.lightboxUrl = null;
  this.lightboxName = '';
}

// ── Document download ──
getDocLabel(path: string): string {
  if (!path) return '';
  return path.substring(path.lastIndexOf('/') + 1);
}


  loadData(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.http.get<ApiResponse>(this.urls.applicantRegistryAllUrl, { headers: this.getHeaders() })
      .subscribe({
        next: (res) => {
          if (!res.success) {
            this.errorMessage = 'Server returned an unsuccessful response.';
            this.isLoading = false;
            return;
          }
          this.allApplicants = res.data || [];
          this.isLoading = false;
          this.applyFilters();
        },
        error: (err) => {
          this.errorMessage = err?.error?.message || 'Failed to load applicants. Please try again.';
          this.isLoading = false;
        }
      });
  }

  toggleRow(id: number): void {
    this.expandedRowId = this.expandedRowId === id ? null : id;
  }

  setFilter(filter: string): void {
    this.activeFilter = filter;
    this.currentPage = 1;
    this.applyFilters();
  }

  onSearch(): void {
    this.currentPage = 1;
    this.applyFilters();
  }

  applyFilters(): void {
    const q = this.searchQuery.toLowerCase().trim();
    this.filteredApplicants = this.allApplicants.filter(a => {
      const matchFilter =
        this.activeFilter === 'ALL' ||
        (this.activeFilter === 'Pending' && (!a.applicationStatus || a.applicationStatus === 'Pending')) ||
        (this.activeFilter === 'Under Review' && a.applicationStatus === 'Under Review') ||
        (this.activeFilter === 'Accepted' && a.applicationStatus === 'Accepted') ||
        (this.activeFilter === 'Rejected' && a.applicationStatus === 'Rejected');

      const fullName = ((a.firstName || '') + ' ' + (a.lastName || '')).toLowerCase();
      const matchSearch = !q ||
        fullName.includes(q) ||
        (a.email || '').toLowerCase().includes(q) ||
        (a.programmeAppliedFor || '').toLowerCase().includes(q) ||
        (a.applicantType || '').toLowerCase().includes(q);

      return matchFilter && matchSearch;
    });
    this.buildPagination();
  }

  buildPagination(): void {
    this.totalPages = Math.max(1, Math.ceil(this.filteredApplicants.length / this.pageSize));
    if (this.currentPage > this.totalPages) this.currentPage = 1;
    this.pages = Array.from({ length: this.totalPages }, (_, i) => i + 1);
    this.slicePage();
  }

  slicePage(): void {
    const start = (this.currentPage - 1) * this.pageSize;
    this.pagedApplicants = this.filteredApplicants.slice(start, start + this.pageSize);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.slicePage();
  }

  // ── Status counts ──
  get statAll(): number { return this.allApplicants.length; }
  get statPending(): number { return this.allApplicants.filter(a => !a.applicationStatus || a.applicationStatus === 'Pending').length; }
  get statUnderReview(): number { return this.allApplicants.filter(a => a.applicationStatus === 'Under Review').length; }
  get statAccepted(): number { return this.allApplicants.filter(a => a.applicationStatus === 'Accepted').length; }
  get statRejected(): number { return this.allApplicants.filter(a => a.applicationStatus === 'Rejected').length; }
  get statSHS(): number { return this.allApplicants.filter(a => a.applicantType === 'SHS Graduate').length; }
  get statDiploma(): number { return this.allApplicants.filter(a => a.applicantType === 'Diploma Holder').length; }

  // ── Modal ──
  openModal(applicant: ApplicantRegistryDto, action: 'Accepted' | 'Rejected' | 'Under Review', event: Event): void {
    event.stopPropagation();
    this.modalApplicant = applicant;
    this.modalAction = action;
    this.reviewerComments = '';
    this.submitSuccess = '';
    this.submitError = '';
    this.showModal = true;
  }

  closeModal(): void {
    this.showModal = false;
    this.modalApplicant = null;
    this.modalAction = '';
    this.reviewerComments = '';
  }

  confirmStatusUpdate(): void {
    if (!this.modalApplicant || !this.modalAction) return;

    this.isSubmitting = true;
    this.submitError = '';
    this.submitSuccess = '';

    const payload: StatusUpdatePayload = {
      applicantId: this.modalApplicant.applicantId,
      applicationStatus: this.modalAction,
      reviewerComments: this.reviewerComments,
      updatedBy: 'registrar'
    };

    this.http.put<any>(this.urls.applicantRegistryStatusUpdateUrl, payload, { headers: this.getHeaders() })
      .subscribe({
        next: (res) => {
          this.isSubmitting = false;
          if (res.success) {
            this.submitSuccess = 'Status updated to ' + this.modalAction + ' successfully.';
            // Update locally without reloading
            const idx = this.allApplicants.findIndex(a => a.applicantId === this.modalApplicant!.applicantId);
            if (idx > -1) {
              this.allApplicants[idx].applicationStatus = this.modalAction;
              this.allApplicants[idx].reviewerComments = this.reviewerComments;
            }
            this.applyFilters();
            setTimeout(() => this.closeModal(), 1500);
          } else {
            this.submitError = res.message || 'Update failed.';
          }
        },
        error: (err) => {
          this.isSubmitting = false;
          this.submitError = err?.error?.message || 'Failed to update status. Please try again.';
        }
      });
  }

  // ── Helpers ──
  getInitials(a: ApplicantRegistryDto): string {
    return (((a.firstName || '?')[0]) + ((a.lastName || '?')[0])).toUpperCase();
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  getStatusClass(status: string): string {
    if (status === 'Accepted') return 'badge-accepted';
    if (status === 'Rejected') return 'badge-rejected';
    if (status === 'Under Review') return 'badge-under-review';
    return 'badge-pending';
  }

  getStatusLabel(status: string): string {
    if (status === 'Accepted') return '✓ Accepted';
    if (status === 'Rejected') return '✕ Rejected';
    if (status === 'Under Review') return '🔍 Under Review';
    return '⏳ Pending';
  }

  get pageStart(): number { return (this.currentPage - 1) * this.pageSize + 1; }
  get pageEnd(): number { return Math.min(this.currentPage * this.pageSize, this.filteredApplicants.length); }
}